/**
 * RateCalculationService - Zentraler Service für Zinssatz- und Renditeberechnungen
 *
 * Dieser Service stellt einheitliche Berechnungsmethoden für alle Simulationsengines bereit
 * und eliminiert Code-Duplikate zwischen SimulationEngine und OneTopfSimulationEngine.
 *
 * Hauptfunktionen:
 * - Berechnung von Inflationsraten basierend auf verschiedenen Strategien
 * - Generierung von Zinssätzen für Verbrauchstopf (Tagesgeld-ähnliche Anlagen)
 * - Bestimmung von Renditen für Investitionstopf (Aktien/ETF-basierte Anlagen)
 * - Kalkulation von Zinstopf-Renditen (Staatsanleihen/Festgeld-ähnliche Anlagen)
 * - Normalisierte Zufallszahlengenerierung für Monte-Carlo-Simulationen
 *
 * @author Portfolio-3Topf Team
 * @version 2.0.0
 * @since 1.0.0
 */

import { SteuerParameter } from '@/types';
import {
  getGermanInflationStatistics,
  getGermanTagesgeldStatistics,
  getGermanZinstopfStatistics,
} from '@/utils/inflationConstants';
import { getIndexStatistics } from '@/utils/indexConstants';
import { GARCHRateCalculationIntegration, GARCHUtils } from '@/utils/garchModeling';

/**
 * Konfigurationsoptionen für Rate-Berechnungen
 *
 * Definiert die Berechnungsstrategien für verschiedene Asset-Klassen und Wirtschaftsindikatoren.
 * Diese Optionen bestimmen, ob historische Daten, statistische Modelle oder Parameter verwendet werden.
 */
export interface RateCalculationOptions {
  /** Inflationsstrategie: 'keine-inflation' | 'parameter-inflation' | 'historische-inflation' | 'statistisch-inflation' */
  inflationOption: string;

  /** Investitionsrendite-Strategie: 'keine-rendite' | 'konstante-rendite' | 'historische-rendite' | 'statistisch-rendite' | 'historische-garch' | 'statistisch-garch' */
  investmentReturnOption: string;

  /** Tagesgeld-Zinsstrategie: 'keine-rendite' | 'historisch-tagesgeld' | 'statistisch-tagesgeld' */
  tagesgeldOption: string;

  /** Zinstopf-Strategie (nur für 3-Topf-Simulationen): 'keine-rendite' | 'historisch-zinstopf' | 'statistisch-zinstopf' */
  zinstopfOption?: string;
}

/**
 * RateCalculationService - Hauptklasse für alle Rate-Berechnungen
 *
 * Diese Klasse bietet statische Methoden für die Berechnung von Zinssätzen und Renditen
 * basierend auf verschiedenen Strategien (Parameter, historische Daten, statistische Modelle).
 *
 * Alle Methoden verwenden sichere Fallback-Werte bei Fehlern und gewährleisten
 * konsistente Ergebnisse über mehrere Simulationsläufe hinweg.
 */
export class RateCalculationService {
  /**
   * Validates input parameters for rate calculations
   * @param mean - The mean value to validate
   * @param stdDev - The standard deviation to validate
   * @param year - Optional year parameter to validate
   * @throws Error if parameters are invalid
   */
  private static validateCalculationParameters(mean: number, stdDev: number, year?: number): void {
    // Check for NaN values
    if (isNaN(mean) || isNaN(stdDev)) {
      throw new Error(`Invalid calculation parameters: mean=${mean}, stdDev=${stdDev}`);
    }

    // Check for negative volatility/standard deviation
    if (stdDev < 0) {
      throw new Error(`Standard deviation cannot be negative: ${stdDev}`);
    }

    // Check for unrealistic financial values (e.g., > 1000% or < -100%)
    if (Math.abs(mean) > 1000) {
      // Unusually high mean value detected (logged for debugging)
    }

    if (stdDev > 200) {
      // Unusually high volatility detected (logged for debugging)
    }

    // Validate year parameter if provided
    if (year !== undefined && (year < 0 || !Number.isInteger(year))) {
      throw new Error(`Invalid year parameter: ${year}`);
    }
  }

  /**
   * splitmix32-Finalizer: mischt einen 32-Bit-Integer avalanched zu einem Uniform [0,1).
   *
   * RNG-Qualitätsfix (Autokorrelation entfernt): Benachbarte Eingaben (x, x+1) liefern
   * durch die Avalanche-Multiplikationen völlig unkorrelierte Outputs. Dadurch erzeugen
   * benachbarte (year, year+1)-Seeds unabhängige Jahresraten (vorher: Lag-1-Autokorrelation
   * ~+0,22 durch schwache XOR-Seed-Ableitung + 1-2 LCG-Schritte).
   */
  private static splitmix32(a: number): number {
    a = (a + 0x9e3779b9) | 0;
    let t = a ^ (a >>> 16);
    t = Math.imul(t, 0x21f0aaad);
    t = t ^ (t >>> 15);
    t = Math.imul(t, 0x735a2d97);
    t = t ^ (t >>> 15);
    return (t >>> 0) / 4294967296;
  }

  /**
   * Mischt (masterSeed, year, iteration, seedOffset) über einen murmur3-Finalizer (fmix32)
   * zu einem avalanched 32-Bit-Basiszustand. Ersetzt die frühere lineare XOR-Kombination
   * (masterSeed ^ (year*31 + off) ^ (iteration*37 + off)), die fast-lineare, autokorrelierte
   * Muster erzeugte. Deterministisch für identische Argumente (Cache/Reproduzierbarkeit).
   */
  private static deriveSeedState(
    masterSeed: number,
    year: number,
    iteration: number,
    seedOffset: number
  ): number {
    let h = masterSeed | 0;
    const mix = (value: number): void => {
      let k = Math.imul(value | 0, 0xcc9e2d51);
      k = (k << 15) | (k >>> 17);
      k = Math.imul(k, 0x1b873593);
      h ^= k;
      h = (h << 13) | (h >>> 19);
      h = (Math.imul(h, 5) + 0xe6546b64) | 0;
    };
    mix(year);
    mix(iteration);
    mix(seedOffset);
    // murmur3 finalizer (fmix32) — vollständige Durchmischung aller Bits
    h ^= h >>> 16;
    h = Math.imul(h, 0x85ebca6b);
    h ^= h >>> 13;
    h = Math.imul(h, 0xc2b2ae35);
    h ^= h >>> 16;
    return h >>> 0;
  }

  /**
   * Enhanced normal distribution generator with bounds checking
   * @param mean - Mean of the distribution
   * @param stdDev - Standard deviation of the distribution
   * @param allowNegative - Whether to allow negative results
   * @returns Normally distributed random value
   */
  private static generateNormalDistribution(
    mean: number,
    stdDev: number,
    allowNegative: boolean = false
  ): number {
    // Validate input parameters
    this.validateCalculationParameters(mean, stdDev);

    const u1 = Math.random();
    const u2 = Math.random();
    const z0 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    const result = mean + stdDev * z0;

    // Check for extreme outliers (beyond 4 standard deviations)
    if (Math.abs(result - mean) > 4 * stdDev) {
      // Extreme outlier detected in normal distribution, clamping to reasonable range
      const clampedResult = mean + Math.sign(result - mean) * 4 * stdDev;
      return allowNegative ? clampedResult : Math.max(0, clampedResult);
    }

    return allowNegative ? result : Math.max(0, result);
  }

  /**
   * Enhanced seeded normal distribution generator with validation
   * @param mean - Mean of the distribution
   * @param stdDev - Standard deviation of the distribution
   * @param year - Year for seeding
   * @param seedOffset - Additional seed offset
   * @param allowNegative - Whether to allow negative results
   * @returns Seeded normally distributed random value
   */
  private static generateSeededNormalDistribution(
    mean: number,
    stdDev: number,
    year: number,
    seedOffset: number = 0,
    allowNegative: boolean = false,
    iteration?: number
  ): number {
    // Validate input parameters
    this.validateCalculationParameters(mean, stdDev, year);

    const masterSeed = this.getMasterSeed(seedOffset);
    const iterationValue = iteration || 0;

    // RNG-Qualitätsfix (Autokorrelation entfernt): Statt schwacher XOR-Seed-Ableitung
    // + 2 LCG-Schritten desselben Zustands werden (masterSeed, year, iteration, seedOffset)
    // über einen murmur3-Finalizer zu einem avalanched Basiszustand gemischt.
    const baseState = this.deriveSeedState(masterSeed, year, iterationValue, seedOffset);

    // Box-Muller braucht ZWEI unabhängige Uniforms. Sie werden aus zwei verschieden
    // gesalzenen, je einzeln avalanchten Zuständen abgeleitet (NICHT aus zwei
    // aufeinanderfolgenden LCG-Schritten desselben schwachen Zustands).
    // u1 wird gegen exakt 0 abgesichert, damit Math.log(u1) endlich bleibt.
    const u1 = Math.max(this.splitmix32(baseState), Number.MIN_VALUE);
    const u2 = this.splitmix32((baseState ^ 0x85ebca6b) >>> 0);

    const z0 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    const result = mean + stdDev * z0;

    // Check for extreme outliers
    if (Math.abs(result - mean) > 4 * stdDev) {
      // Extreme outlier detected in seeded normal distribution, clamping to reasonable range
      const clampedResult = mean + Math.sign(result - mean) * 4 * stdDev;
      return allowNegative ? clampedResult : Math.max(0, clampedResult);
    }

    return allowNegative ? result : Math.max(0, result);
  }

  /**
   * Berechnet den Zinssatz für den Verbrauchstopf basierend auf der gewählten Strategie
   *
   * Der Verbrauchstopf repräsentiert liquide, risikoarme Anlagen wie Tagesgeld, die für
   * laufende Ausgaben und kurzfristige Liquiditätsbedürfnisse verwendet werden.
   *
   * @param options - Konfigurationsoptionen mit Tagesgeld-Strategie
   * @param steuerParameter - Steuerliche und finanzielle Grundparameter
   * @returns Zinssatz in Prozent (z.B. 2.5 für 2,5% p.a.)
   *
   * @example
   * ```typescript
   * const rate = RateCalculationService.calculateVerbrauchstopfRate(
   *   { tagesgeldOption: 'historisch-tagesgeld', inflationOption: 'historische-inflation' },
   *   steuerParameter
   * );
   * console.log(`Verbrauchstopf-Zinssatz: ${rate.toFixed(2)}%`);
   * ```
   *
   * @throws Gibt bei Fehlern 0% zurück und loggt Fehlerdetails
   */
  static calculateVerbrauchstopfRate(
    options: RateCalculationOptions,
    steuerParameter: SteuerParameter,
    year?: number,
    iteration?: number
  ): number {
    try {
      switch (options.tagesgeldOption) {
        case 'keine-rendite':
          return 0;
        case 'parameter-rendite':
          return steuerParameter.zinssatzVerbrauchstopf;
        case 'historisch-tagesgeld':
          const stats = getGermanTagesgeldStatistics();
          // Bootstrap-Sampling aus historischen deutschen Tagesgeld-Zinssätzen
          return this.selectFromBootstrapSample(stats.rates, year, 6000, iteration);
        case 'statistisch-tagesgeld':
          const historicalStats = getGermanTagesgeldStatistics();
          // Bewusste 0-Untergrenze (letzter Parameter `allowNegative = false` unten):
          // Tagesgeld ist ein taeglich kuendigbares Bankguthaben, fuer das deutsche
          // Banken Privatkunden praktisch nie einen NEGATIVEN Zins berechnen (anders
          // als z.B. Anleiherenditen). Ohne diesen Floor wuerde die Normalverteilung
          // (Mittelwert/Stddev aus der historischen Statistik) bei Ziehungen weit im
          // linken Tail negative Zinssaetze erzeugen, die es fuer diese Anlageklasse
          // real so nicht gibt. Quantifizierter Effekt dieses Clippings: ca. 12,3% aller
          // Ziehungen faellt auf 0 zusammengestaucht, wodurch der Mittelwert der
          // generierten Reihe um ca. +0,07 Prozentpunkte ueber dem historischen
          // Mittelwert liegt (leichter Optimismus-Bias durch den Floor, hier bewusst
          // in Kauf genommen statt eine unrealistische Negativverzinsung zuzulassen).
          if (year !== undefined) {
            // Use crypto-seeded normal distribution with tagesgeld-specific offset
            return this.generateSeededNormalDistribution(
              historicalStats.mean,
              historicalStats.stdDev,
              year + 1000,
              6000,
              false,
              iteration
            );
          } else {
            return this.generateNormalDistribution(
              historicalStats.mean,
              historicalStats.stdDev,
              false
            );
          }
        default:
          return 0;
      }
    } catch (error) {
      console.error('Error calculating Verbrauchstopf rate:', error);
      return 0; // Safe fallback
    }
  }

  /**
   * Berechnet den Zinssatz für den Zinstopf basierend auf der gewählten Strategie
   *
   * Der Zinstopf repräsentiert mittelfristige, sichere Anlagen wie Staatsanleihen oder
   * Festgeld mit fester Laufzeit. Diese Anlageklasse bietet Stabilität und planbare Erträge.
   *
   * @param options - Konfigurationsoptionen mit Zinstopf-Strategie
   * @param steuerParameter - Steuerliche und finanzielle Grundparameter
   * @returns Zinssatz in Prozent (z.B. 3.2 für 3,2% p.a.)
   *
   * @example
   * ```typescript
   * const rate = RateCalculationService.calculateZinstopfRate(
   *   { zinstopfOption: 'statistisch-zinstopf', inflationOption: 'parameter-inflation' },
   *   steuerParameter
   * );
   * console.log(`Zinstopf-Zinssatz: ${rate.toFixed(2)}%`);
   * ```
   *
   * @throws Gibt bei Fehlern 0% zurück und loggt Fehlerdetails
   * @note Für 1-Topf-Simulationen wird diese Methode nicht aufgerufen (zinstopfOption ist undefined)
   */
  static calculateZinstopfRate(
    options: RateCalculationOptions,
    steuerParameter: SteuerParameter,
    year?: number,
    iteration?: number
  ): number {
    // For 1-Topf simulations, this method won't be called as there's no Zinstopf
    if (!options.zinstopfOption) {
      return 0;
    }

    try {
      switch (options.zinstopfOption) {
        case 'keine-rendite':
          return 0;
        case 'parameter-rendite':
          return steuerParameter.zinssatzZinstopf;
        case 'historisch-zinstopf':
          const stats = getGermanZinstopfStatistics();
          // Bootstrap-Sampling aus historischen deutschen Staatsanleihen-Renditen
          return this.selectFromBootstrapSample(stats.performances, year, 8000, iteration);
        case 'statistisch-zinstopf':
          const historicalStats = getGermanZinstopfStatistics();
          if (year !== undefined) {
            // Use crypto-seeded normal distribution with zinstopf-specific offset
            return this.generateSeededNormalDistribution(
              historicalStats.mean,
              historicalStats.stdDev,
              year + 4000,
              9000,
              false,
              iteration
            );
          } else {
            return this.generateNormalDistribution(
              historicalStats.mean,
              historicalStats.stdDev,
              false
            );
          }
        default:
          return 0;
      }
    } catch (error) {
      console.error('Error calculating Zinstopf rate:', error);
      return 0; // Safe fallback
    }
  }

  /**
   * Berechnet die Rendite für den Investitionstopf basierend auf der gewählten Strategie
   *
   * Der Investitionstopf repräsentiert langfristige, wachstumsorientierte Anlagen wie Aktien-ETFs,
   * die historisch die höchsten Renditen bieten, aber auch die größte Volatilität aufweisen.
   *
   * @param options - Konfigurationsoptionen mit Investment-Rendite-Strategie
   * @param steuerParameter - Steuerliche und finanzielle Grundparameter
   * @returns Rendite in Prozent (z.B. 7.5 für 7,5% p.a., kann bei Marktkrisen negativ sein)
   *
   * @example
   * ```typescript
   * const rate = RateCalculationService.calculateInvestitionstopfRate(
   *   { investmentReturnOption: 'historische-garch', inflationOption: 'keine-inflation' },
   *   steuerParameter
   * );
   * console.log(`Investitionstopf-Rendite mit GARCH: ${rate.toFixed(2)}%`);
   * ```
   *
   * @throws Gibt bei Fehlern 0% zurück und loggt Fehlerdetails
   * @note Bei 'konstante-rendite' wird der Parameter ohne Volatilität verwendet (deterministisch)
   * @note Bei 'historische-garch' wird zeitvariable GARCH-Volatilität mit historischen Daten kombiniert
   * @note Bei 'statistisch-garch' wird EGARCH-Modell mit statistischer Verteilung verwendet
   */
  static calculateInvestitionstopfRate(
    options: RateCalculationOptions,
    steuerParameter: SteuerParameter,
    year?: number,
    iteration?: number
  ): number {
    try {
      switch (options.investmentReturnOption) {
        case 'keine-rendite':
          return 0;
        case 'konstante-rendite':
          // Konstant heißt konstant: exakter Parameterwert, auch im Monte-Carlo.
          // (Früher wurde hier ein Sinus-"Rauschen" von ±0,01% aufgeprägt, das
          // deterministische Szenarien scheinbar stochastisch machte: Schein-
          // Perzentilspreizung, Schein-Volatilität und explodierende Sharpe-Ratios.)
          return steuerParameter.renditeInvestitionstopf;
        case 'historische-rendite':
          // Default to MSCI World for historical returns
          const worldStats = getIndexStatistics('world');
          // Bootstrap-Sampling aus historischen MSCI World Jahresrenditen
          return this.selectFromBootstrapSample(worldStats.rates, year, 7000, iteration);
        case 'statistisch-rendite':
          // Default to MSCI World for statistical returns
          const worldHistoricalStats = getIndexStatistics('world');
          if (year !== undefined) {
            // Use crypto-seeded normal distribution with investment-specific offset, allow negative values for market crashes
            return this.generateSeededNormalDistribution(
              worldHistoricalStats.mean,
              worldHistoricalStats.stdDev,
              year + 3000,
              8000,
              true,
              iteration
            );
          } else {
            // Allow negative values for realistic market crash simulation
            return this.generateNormalDistribution(
              worldHistoricalStats.mean,
              worldHistoricalStats.stdDev,
              true
            );
          }
        case 'historische-garch':
          // GARCH-enhanced historical returns with volatility clustering
          return GARCHRateCalculationIntegration.generateSingleGARCHRate(
            'historische-garch',
            'world'
          );
        case 'statistisch-garch':
          // GARCH-enhanced statistical returns with time-varying volatility
          return GARCHRateCalculationIntegration.generateSingleGARCHRate(
            'statistisch-garch',
            'world'
          );
        default:
          return 0;
      }
    } catch (error) {
      console.error('Error calculating Investitionstopf rate:', error);
      return 0; // Safe fallback
    }
  }

  /**
   * Generiert eine Sequenz von Investitionstopf-Renditen mit GARCH-Eigenschaften
   *
   * Diese Methode wird für Multi-Jahr-Simulationen verwendet, wo die zeitvariable
   * GARCH-Volatilität über mehrere Jahre hinweg konsistent modelliert werden muss.
   *
   * @param options - Konfigurationsoptionen mit Investment-Rendite-Strategie
   * @param years - Anzahl der Jahre für die Simulation
   * @param indexType - Index-Typ ('world' oder 'acwi') für GARCH-Kalibrierung
   * @param seed - Optional: Seed für reproduzierbare Ergebnisse
   * @returns Array von jährlichen Renditen in Prozent
   *
   * @example
   * ```typescript
   * const rates = RateCalculationService.generateGARCHRateSequence(
   *   { investmentReturnOption: 'historische-garch' },
   *   30, // 30 Jahre
   *   'world',
   *   12345 // Seed für Reproduzierbarkeit
   * );
   * console.log(`30-Jahr GARCH-Sequenz: ${rates.map(r => r.toFixed(1)).join(', ')}%`);
   * ```
   *
   * @throws Gibt bei Fehlern Array mit 0% Werten zurück und loggt Fehlerdetails
   * @note Nur für GARCH-Optionen relevant - andere Optionen fallen auf Einzelrate-Generierung zurück
   * @note GARCH-Zustand wird automatisch über die gesamte Sequenz konsistent verwaltet
   */
  static generateGARCHRateSequence(
    options: RateCalculationOptions,
    years: number,
    indexType: 'world' | 'acwi' = 'world',
    seed?: number
  ): number[] {
    try {
      // Check if this is a GARCH option
      if (GARCHUtils.isGARCHOption(options.investmentReturnOption)) {
        const garchOption = options.investmentReturnOption as
          | 'historische-garch'
          | 'statistisch-garch';
        return GARCHRateCalculationIntegration.generateInvestitionstopfReturns(
          garchOption,
          years,
          indexType,
          seed
        );
      } else {
        // Fallback to individual rate generation for non-GARCH options
        return Array.from({ length: years }, () =>
          this.calculateInvestitionstopfRate(options, {
            renditeInvestitionstopf: 7.0,
          } as SteuerParameter)
        );
      }
    } catch (error) {
      console.error('Error generating GARCH rate sequence:', error);
      return Array(years).fill(0); // Safe fallback
    }
  }

  /**
   * Analysiert GARCH-Parameter für UI-Anzeige und Bildungszwecke
   *
   * Diese Methode stellt GARCH-Kalibrierungsdetails für die Benutzeroberfläche bereit,
   * um Transparenz über die verwendeten Modellparameter zu schaffen.
   *
   * @param indexType - Index-Typ ('world' oder 'acwi') für GARCH-Analyse
   * @returns Formatierte GARCH-Analysedaten für UI-Display
   *
   * @example
   * ```typescript
   * const analysis = RateCalculationService.getGARCHAnalysis('world');
   * console.log(`GARCH-Parameter für ${analysis.indexType}:`);
   * console.log(`Volatilitäts-Persistenz: ${analysis.garchParameters.volatilityPersistence}`);
   * console.log(`Leverage-Effekt: ${analysis.garchParameters.leverageEffect}`);
   * ```
   *
   * @note Verwendet die gleichen Parameter wie die aktuelle GARCH-Generierung
   * @note Hilfreich für Tooltips und erweiterte Parameteranzeigen
   */
  static getGARCHAnalysis(indexType: 'world' | 'acwi' = 'world') {
    try {
      return GARCHRateCalculationIntegration.analyzeIndexDataForGARCH(indexType);
    } catch (error) {
      console.error('Error analyzing GARCH data:', error);
      return {
        indexType,
        dataPoints: 0,
        historicalMean: '0.00',
        historicalVolatility: '0.00',
        skewness: '0.000',
        kurtosis: '0.000',
        garchParameters: {
          volatilityPersistence: '0.000',
          archEffect: '0.000',
          leverageEffect: '0.000',
          clusteringEffect: '0.000',
        },
      };
    }
  }

  /**
   * Berechnet die Inflationsrate basierend auf der gewählten Strategie
   *
   * Die Inflationsrate ist entscheidend für die Kaufkraftentwicklung und beeinflusst
   * die reale Wertentwicklung aller Portfoliobestandteile über die Simulationsdauer.
   *
   * @param options - Konfigurationsoptionen mit Inflationsstrategie
   * @param steuerParameter - Steuerliche und finanzielle Grundparameter mit Basis-Inflationsrate
   * @param year - Optional: Simulationsjahr für deterministische historische Auswahl (0-basiert)
   * @returns Inflationsrate in Prozent (z.B. 2.1 für 2,1% p.a.)
   *
   * @example
   * ```typescript
   * const inflationRate = RateCalculationService.calculateInflationRate(
   *   { inflationOption: 'historische-inflation', selectedKapitalbedarf: 'standard' },
   *   steuerParameter,
   *   5 // Jahr 5 der Simulation
   * );
   * console.log(`Inflation: ${inflationRate.toFixed(2)}%`);
   * ```
   *
   * @throws Gibt bei Fehlern 0% zurück und loggt Fehlerdetails
   * @note Historische Daten basieren auf deutscher Verbraucherpreisindex-Statistik (1992-2023)
   * @note Bei historischen Daten: Wenn Jahr angegeben, wird deterministisch ausgewählt (Jahr % Anzahl historischer Werte)
   * @note Statistische Werte verwenden Normalverteilung um historischen Mittelwert mit realer Standardabweichung
   */
  static calculateInflationRate(
    options: RateCalculationOptions,
    steuerParameter: SteuerParameter,
    year?: number,
    iteration?: number
  ): number {
    try {
      switch (options.inflationOption) {
        case 'keine-inflation':
          return 0;
        case 'parameter-inflation':
          return steuerParameter.inflationsrate;
        case 'historische-inflation':
          const stats = getGermanInflationStatistics();
          // Bootstrap-Sampling aus historischen deutschen Inflationsdaten
          const historicalResult = this.selectFromBootstrapSample(
            stats.rates,
            year,
            5000,
            iteration
          );

          return historicalResult;
        case 'statistisch-inflation':
          const historicalStats = getGermanInflationStatistics();
          if (year !== undefined) {
            // Use crypto-seeded normal distribution with inflation-specific offset.
            // allowNegative = true: the German CPI history contains deflation years
            // (e.g. 1953, 1986), so flooring at 0 would truncate the distribution and
            // bias the expected inflation upward.
            const statisticalResult = this.generateSeededNormalDistribution(
              historicalStats.mean,
              historicalStats.stdDev,
              year,
              5000,
              true,
              iteration
            );

            return statisticalResult;
          } else {
            return this.generateNormalDistribution(
              historicalStats.mean,
              historicalStats.stdDev,
              true
            );
          }
        default:
          return 0;
      }
    } catch (error) {
      console.error('Error calculating inflation rate:', error);
      return 0; // Safe fallback
    }
  }

  /**
   * Bestimmt anhand der Investitionstopf-Rendite, ob sich der Markt in einer Krise befindet
   *
   * Diese Methode definiert eine einfache Regel zur Marktkrisenerkennung basierend auf
   * negativen Renditen. In Krisenzeiten können steuerliche Optimierungen und Verkaufsstrategien
   * angepasst werden.
   *
   * @param investitionstopfRendite - Die berechnete Rendite des Investitionstopfs in Prozent
   * @returns true wenn Marktkrise (Rendite < 0%), false bei positiver Marktentwicklung
   *
   * @example
   * ```typescript
   * const rendite = -15.3; // Starker Markteinbruch
   * const istKrise = RateCalculationService.istMarktKrise(rendite);
   * console.log(`Marktkrise: ${istKrise ? 'Ja' : 'Nein'}`); // "Marktkrise: Ja"
   * ```
   *
   * @note Diese vereinfachte Definition kann je nach Anlagestrategie erweitert werden
   * @note Historisch treten Marktkrisen ca. 20-25% der Jahre auf (siehe 2000, 2008, 2018, 2020, 2022)
   */
  static istMarktKrise(investitionstopfRendite: number): boolean {
    return investitionstopfRendite < 0;
  }

  // Cache für crypto-generierte Master-Seeds pro Parameter-Typ
  private static masterSeeds: Map<number, number> = new Map();
  // Global counter to ensure different seeds after cache clearing
  private static globalSeedCounter: number = 0;

  /**
   * Generiert einen Master-Seed basierend auf crypto.getRandomValues() für bessere Randomisierung
   * zwischen verschiedenen Simulationsläufen, aber deterministisch für reproduzierbare Ergebnisse.
   */
  private static getMasterSeed(seedOffset: number): number {
    // Include global seed counter to ensure different seeds after cache clearing
    const effectiveSeedOffset = seedOffset + (this.globalSeedCounter || 0) * 10000;

    if (!this.masterSeeds.has(effectiveSeedOffset)) {
      const cryptoArray = new Uint32Array(1);
      crypto.getRandomValues(cryptoArray);

      this.masterSeeds.set(effectiveSeedOffset, cryptoArray[0]);
    }
    return this.masterSeeds.get(effectiveSeedOffset)!;
  }

  /**
   * Löscht alle gecachten Master-Seeds um neue crypto-basierte Randomisierung zu erzwingen.
   * Nützlich für komplett neue Simulationsreihen mit frischer Zufälligkeit.
   */
  public static clearMasterSeeds(): void {
    this.masterSeeds.clear();
    // Also increment a global seed counter to ensure different seedOffsets
    this.globalSeedCounter = (this.globalSeedCounter || 0) + 1;
  }

  /**
   * Führt Bootstrap-Sampling durch: Auswahl eines Werts mit Zurücklegen aus historischen Daten
   *
   * Bootstrap-Sampling für Simulationen bedeutet, dass wir für jedes Simulationsjahr
   * einen Wert zufällig (mit Zurücklegen) aus dem historischen Datensatz auswählen.
   * Dies erhält die ursprünglichen Datencharakteristika bei natürlicher Variation.
   *
   * Verwendet crypto.getRandomValues() für Master-Seeds pro Parameter-Typ und deterministische
   * Jahr-basierte Variation für reproduzierbare, aber gut verteilte Bootstrap-Samples.
   *
   * @param originalData - Der ursprüngliche historische Datensatz
   * @param year - Simulationsjahr (wird für Seed-Variation verwendet)
   * @param seedOffset - Offset für Parameter-Typ-spezifische Master-Seeds
   * @returns Zufällig ausgewählter Wert aus den historischen Daten
   */
  private static selectFromBootstrapSample(
    originalData: number[],
    year?: number,
    seedOffset: number = 0,
    iteration?: number
  ): number {
    if (year !== undefined) {
      // Verwende crypto-generierten Master-Seed für diesen Parameter-Typ
      const masterSeed = this.getMasterSeed(seedOffset);
      const iterationValue = iteration || 0;

      // RNG-Qualitätsfix (Autokorrelation entfernt): avalanched Mischung statt
      // schwacher XOR-Kombination + 2 LCG-Schritten. Benachbarte Jahre wählen dadurch
      // unkorrelierte Datenpunkte (vorher: ~37% gleicher/benachbarter Punkt im Folgejahr).
      const baseState = this.deriveSeedState(masterSeed, year, iterationValue, seedOffset);
      const normalizedRandom = this.splitmix32(baseState);

      // Bootstrap-Sampling Index
      const randomIndex = Math.floor(normalizedRandom * originalData.length);
      return originalData[randomIndex];
    } else {
      // Standard Bootstrap-Sampling mit Math.random()
      const randomIndex = Math.floor(Math.random() * originalData.length);
      return originalData[randomIndex];
    }
  }
}
